import { useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { notificationsAPI, socialAPI } from '@/lib/api';

const isVnxContext = () =>
    /^(vnx|inside)\./i.test(window.location.hostname) ||
    window.location.pathname.startsWith('/inside-vnx');

/**
 * Affiche un badge numérique sur l'icône de l'app installée (PWA) avec le nombre
 * de notifications non vues — comme une vraie application native.
 * Utilise la Badging API (navigator.setAppBadge). Pris en charge sur les PWA
 * installées (Chrome/Edge desktop & Android, iOS 16.4+ en écran d'accueil).
 * Sur les navigateurs non compatibles, ne fait simplement rien.
 *
 * - App Inside VNX (vnx.*) : notifications sociales + messages privés non lus.
 * - App CRM : notifications CRM non lues.
 */
export function useAppBadge() {
    const { user } = useAuth();

    useEffect(() => {
        if (!user) return;
        if (typeof navigator === 'undefined' || !('setAppBadge' in navigator)) return;

        let cancelled = false;
        const vnx = isVnxContext();

        const setBadge = (total) => {
            if (cancelled) return;
            if (total > 0) navigator.setAppBadge(total).catch(() => {});
            else navigator.clearAppBadge?.().catch(() => {});
        };

        const fetchCount = async () => {
            try {
                let total = 0;
                if (vnx) {
                    const [notif, convo] = await Promise.allSettled([
                        socialAPI.unreadCount(),
                        socialAPI.conversationsUnread(),
                    ]);
                    if (notif.status === 'fulfilled') total += notif.value.data?.count || 0;
                    if (convo.status === 'fulfilled') total += convo.value.data?.count || 0;
                } else {
                    const res = await notificationsAPI.getAll(1);
                    total = res.data?.unread_count || 0;
                }
                setBadge(total);
            } catch { /* silencieux : le badge est best-effort */ }
        };

        fetchCount();
        const interval = setInterval(fetchCount, 30000);
        const onVisible = () => { if (document.visibilityState === 'visible') fetchCount(); };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user]);
}

export default useAppBadge;
